<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("tipos_documentos_operacion.grid.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearTipoDocumentoOperacion()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Nombre" :caption="$t('tipos_documentos_operacion.grid.nombre')" data-type="string"></DxColumn>
      <DxColumn data-field="AplicaPersonaFisica" :caption="$t('tipos_documentos_operacion.grid.aplica_fisica')"
        data-type="boolean" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="AplicaPersonaJuridica" :caption="$t('tipos_documentos_operacion.grid.aplica_juridica')"
        data-type="boolean" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('tipos_documentos_operacion.grid.acciones')" :fixed="true" width="100px"
        fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('ofertas.grid.modificar'),
          icon: 'fa-regular fa-edit text-success',
          onClick: (e: any) => verDetalle(e.row?.data.Id),
          disabled: (options: any) => options.row?.data.GestionadaSistema
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('ofertas.grid.borrar'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => borrar(e.row?.data.Id),
          disabled: (options: any) => options.row?.data.GestionadaSistema
        }
      ]" />

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxLookup, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getColumnLookupBoolean } from "@helpers/grid-helper";
import { useApi } from "@api/integration";  
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { type DXLoadOptions } from "@/.shared/.api";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { CreateTipoDocumentoEmpresaServicios } from "./models/CreateTipoDocumentoEmpresaServicios";
import TipoDocumentoEmpresaServiciosForm from "./TipoDocumentoEmpresaServiciosForm.vue";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();

const props = defineProps({
  idEmpresaServicios: String
});

const defaultPopUpProps = { width: 500, height: 300 };

onMounted(async () => {
  setDatasource();
});

async function crearTipoDocumentoOperacion() {
  const title = `${intl.i18n.t("tipos_documentos_operacion.creacion.titulo")}`;

  let modalProps: Record<string, unknown>;
  modalProps = { model: new CreateTipoDocumentoEmpresaServicios({ IdEmpresaServicios: props.idEmpresaServicios }), isEditMode: false };

  modal.showModal(TipoDocumentoEmpresaServiciosForm, modalProps, { ...defaultPopUpProps, title: title }).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const setDatasource = async () => {

  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getTiposDocumentosEmpresaServiciosForGrid", { showLoading: false }, props.idEmpresaServicios, q)
    }
  });
}

const verDetalle = async (idTipoDocumentoOperacion: string) => {
  let title = `${intl.i18n.t("tipos_documentos_operacion.edicion.titulo")}`;
  let value = await api.callStandardApi("getTipoDocumentoEmpresaServiciosById", { showLoading: true }, idTipoDocumentoOperacion);
  let modalProps = { model: new CreateTipoDocumentoEmpresaServicios().createFromTipoDocumentoDetail(value), isEditMode: true };
 
  let response = await modal.showModal(TipoDocumentoEmpresaServiciosForm, modalProps , { title: title, ...defaultPopUpProps });

  if (response == messageBoxResult.ok)
    grid.value?.instance?.refresh();
}

const borrar = async (idTipoDocumentoOperacion: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t('tipos_documentos_operacion.grid.borrar_pregunta'), intl.i18n.t('tipos_documentos_operacion.grid.titulo_confirma_borrar'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteTipoDocumentoEmpresaServicios", { showLoading: true }, idTipoDocumentoOperacion)
      .then(() => {
        toastSuccess(intl.i18n.t('tipos_documentos_operacion.borrado_exito'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('tipos_documentos_operacion.borrado_fallo')));
}

</script>

<style lang="scss" scoped>


</style>